// =====================================================================================
// INVRT Radar - web console - the campaign delivery tab (story H07)
//
// One job: let a client choose and SAVE what its organisation would deliver for this
// campaign, and show the server's truth about what surrounds that choice.
//
//   SAVE IS NOT SEND. The save button is "Save selection" because that is all it does: a
//   PUT of the selection document with the engine's CAS revision. Nothing is dispatched,
//   no destination is contacted, and no copy on this surface claims otherwise. What the
//   user sees next is driven by the readiness states the server reports (selection,
//   binding, destination, coverage, per-source attestation) - never inferred here.
//
//   TRUTH RULES. The three preview diagnostics are overlapping groups and are labelled
//   as never-to-be-summed; no total of them is rendered. Coverage and readiness come
//   from the server's vocabulary and are never shown as "Applied" - historical verified
//   is not Applied, and a receipt is a delivery-record outcome, listed on the records
//   surface, distinct from current coverage.
//
// Design system v2.8.0 only: the DS card, chip, field and button grammar, 44px targets,
// no purple. Focus lands on the save status after a save so keyboard users see the
// outcome; the draft is preserved on conflict and rebased explicitly.
// =====================================================================================

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useDeliveryWorkspace } from "./useDelivery";
import { isApiError } from "@/lib/api";
import type { DeliveryPolicy, DeliverySelection } from "@/lib/hiDelivery";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";

const OVERLAP_NOTE = "Do not add these counts together - the groups overlap, so the same lead can appear in more than one.";

const DIAGNOSTIC_LABELS: Record<string, string> = {
  country_not_confirmed_us: "Country not confirmed as US",
  source_unattested: "Source not attested",
  explicit_non_us: "Explicitly non-US",
};

const POLICY_LABELS: Record<string, string> = {
  all_accepted: "All accepted leads",
  high_intent: "High-intent leads only",
  tags: "Chosen tags",
};

function policySummary(policy: DeliveryPolicy): string {
  if (policy.mode === "tags") return `Chosen tags: ${policy.tags.join(", ")}`;
  return POLICY_LABELS[policy.mode] ?? policy.mode;
}

/** The editor's value: the draft when one exists, else the saved selection. */
function editorValue(draftSelection: DeliverySelection | null, saved: DeliveryPolicy | null): DeliverySelection {
  if (draftSelection !== null) return draftSelection;
  if (saved === null) return { version: 1, mode: "high_intent" };
  if (saved.mode === "tags") {
    return { version: 1, mode: "tags", tags: saved.tags.filter((t): t is "A" | "B" | "C" => t === "A" || t === "B" || t === "C") };
  }
  return { version: 1, mode: saved.mode === "all_accepted" ? "all_accepted" : "high_intent" };
}

function validSelection(value: DeliverySelection): boolean {
  return value.mode !== "tags" || value.tags.length > 0;
}

export function CampaignDeliveryTab({ campaignKey }: { campaignKey: string }) {
  const workspace = useDeliveryWorkspace(campaignKey);
  const { selection, readiness, preview, draft, hasDraft, setDraft, clearDraft, save, savePending, saveResult, conflict, dismissConflict } = workspace;

  const [rebasedNote, setRebasedNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);

  const tenantMismatch =
    (selection.isError && isApiError(selection.error) && selection.error.token === "tenant_mismatch") ||
    (readiness.isError && isApiError(readiness.error) && readiness.error.token === "tenant_mismatch");
  const configMissing =
    (selection.isError && isApiError(selection.error) && selection.error.token === "config_missing") ||
    (readiness.isError && isApiError(readiness.error) && readiness.error.token === "config_missing");
  const denied =
    (selection.isError && isApiError(selection.error) && selection.error.httpStatus === 403) ||
    (readiness.isError && isApiError(readiness.error) && readiness.error.httpStatus === 403);
  const failed = selection.isError && !denied;
  const failedRefetch = () => { void selection.refetch(); void readiness.refetch(); };
  const uncertainReadNotice = workspace.uncertainSave
    ? " The save outcome remains unknown. Your draft is kept; this read cannot confirm whether the earlier save completed."
    : "";

  // Focus management: after a completed save, focus lands on the status line, so a
  // keyboard user reads the outcome where their focus went. Nothing moves while the
  // request is in flight.
  useEffect(() => {
    if ((saveResult !== null || workspace.reconciliation !== null) && statusRef.current) statusRef.current.focus();
  }, [saveResult, workspace.reconciliation]);

  if (tenantMismatch) {
    // The engine resolves the organisation from the principal itself. If its answer names
    // a different organisation than the one selected, the console refuses to render a
    // single row of it - this honest refusal IS the next best state.
    return (
      <EmptyState
        tone="error"
        title="This delivery setup belongs to another organisation."
        body={`The delivery service answered for a different organisation than the one selected, so nothing is shown here. Switch back to the other organisation, or re-open this campaign.${uncertainReadNotice}`}
      />
    );
  }
  if (configMissing) {
    return (
      <EmptyState
        tone="error"
        title="Delivery selection is not connected in this build."
        body={`The console build serving this page was not given the HIGH-INTENT delivery endpoint, so nothing can be read or saved in this build. This is a deployment setting, not something to fix at your end.${uncertainReadNotice}`}
      />
    );
  }
  if (denied) {
    return (
      <EmptyState
        tone="error"
        title="You do not have permission to manage delivery selection."
        body={`Ask your RADAR administrator to review access for the selected organisation.${uncertainReadNotice}`}
      />
    );
  }
  if (failed) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorState error={selection.error} deliveryRead onRetry={failedRefetch} />
        {hasDraft ? <p className="text-sm text-ink-2">Your draft is preserved. Retrying does not discard your edits.{uncertainReadNotice}</p> : null}
        {draft?.attempt?.correlationId ? <p className="text-xs text-ink-2">Save correlation id: <code className="font-mono">{draft.attempt.correlationId}</code></p> : null}
      </div>
    );
  }

  // v5 note: a query that is still disabled (its org not resolved yet) is isPending but
  // NOT isLoading - and this surface must not render its interactive editor before the
  // workspace can actually act. The pending gate is the honest loading state.
  if (selection.isPending || readiness.isPending) {
    return <LoadingState label="Loading delivery setup…" />;
  }

  const saved = selection.data?.selection ?? null;
  const revision = selection.data?.revision ?? 0;
  const setupRequired = selection.data?.setupRequired ?? true;
  const value = editorValue(draft?.selection ?? null, saved);

  function onEditorChange(next: DeliverySelection) {
    setProblem(null);
    setRebasedNote(null);
    setDraft(next);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (savePending || conflict !== null || (workspace.uncertainSave && workspace.reconciliation !== "retry_allowed")) return;
    if (!validSelection(value)) {
      setProblem("Choose at least one tag, or pick another selection mode. Nothing was saved.");
      return;
    }
    save(value);
  }

  async function reloadAfterConflict() {
    setRebasedNote(null);
    const result = await selection.refetch();
    if (result.isSuccess && draft !== null) {
      // Rebase the kept draft onto the fresh revision so the next save carries the
      // current CAS base - the edits survive, the stale base does not.
      setDraft(draft.selection);
      setRebasedNote(`Draft kept, now based on revision ${result.data?.revision ?? "unknown"}.`);
    }
    dismissConflict();
  }

  const readinessData = readiness.data ?? null;
  const effectiveMode = value.mode;
  const effectiveTags = value.mode === "tags" ? value.tags : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="inv-card flex flex-col gap-3 p-5" data-pad>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base font-semibold text-ink">Delivery selection</h2>
          {workspace.uncertainSave && workspace.reconciliation === null
            ? <Chip signal="ctx">Save outcome unknown</Chip>
            : setupRequired ? <Chip signal="ctx">Setup required</Chip> : <Chip signal="buy">Selection saved</Chip>}
          <span className="text-xs text-ink-3">revision {revision}</span>
        </div>
        <p className="max-w-3xl text-sm text-ink-2">
          Choose which leads this organisation would deliver for this campaign. Saving and sending are separate steps,
          and this surface only saves.
        </p>
        {setupRequired && !workspace.uncertainSave ? (
          <p className="text-sm text-ink-2">No selection saved yet. Choose what this organisation would deliver below, then save it.</p>
        ) : saved !== null ? (
          <p className="text-sm text-ink-2">
            Saved selection: {policySummary(saved)}
            {selection.data?.updatedAt ? <> · last saved {new Date(selection.data.updatedAt).toLocaleString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} UTC</> : null}
          </p>
        ) : null}
      </div>

      <form noValidate className="flex flex-col gap-6" aria-label="Delivery selection editor" onSubmit={(event) => event.preventDefault()}>
        <div className="inv-card flex flex-col gap-3 p-5" data-pad>
          <fieldset disabled={savePending || workspace.uncertainSave} className="flex flex-col gap-2">
            <legend className="inv-label">What would this organisation deliver?</legend>
            <label className="flex min-h-11 items-center gap-3 text-sm text-ink">
              <input
                type="radio"
                name="delivery-mode"
                value="high_intent"
                checked={effectiveMode === "high_intent"}
                onChange={() => onEditorChange({ version: 1, mode: "high_intent" })}
                className="h-4 w-4 accent-primary"
              />
              High-intent leads only
            </label>
            <label className="flex min-h-11 items-center gap-3 text-sm text-ink">
              <input
                type="radio"
                name="delivery-mode"
                value="all_accepted"
                checked={effectiveMode === "all_accepted"}
                onChange={() => onEditorChange({ version: 1, mode: "all_accepted" })}
                className="h-4 w-4 accent-primary"
              />
              All accepted leads
            </label>
            <label className="flex min-h-11 items-center gap-3 text-sm text-ink">
              <input
                type="radio"
                name="delivery-mode"
                value="tags"
                checked={effectiveMode === "tags"}
                onChange={() => onEditorChange({ version: 1, mode: "tags", tags: effectiveTags.length > 0 ? effectiveTags : ["A"] })}
                className="h-4 w-4 accent-primary"
              />
              Chosen tags
            </label>
          </fieldset>
          {effectiveMode === "tags" ? (
            <fieldset disabled={savePending || workspace.uncertainSave} className="flex flex-col gap-2 rounded-card border border-dashed border-line p-3">
              <legend className="inv-label">Tags to include</legend>
              {(["A", "B", "C"] as const).map((tag) => (
                <label key={tag} className="flex min-h-11 items-center gap-3 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={effectiveTags.includes(tag)}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...effectiveTags, tag].sort()
                        : effectiveTags.filter((t) => t !== tag);
                      onEditorChange({ version: 1, mode: "tags", tags: next });
                    }}
                    className="h-4 w-4 accent-primary"
                  />
                  Tag {tag}
                </label>
              ))}
            </fieldset>
          ) : null}
          {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
        </div>

        <div className="inv-card flex flex-col gap-3 p-5" data-pad>
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold text-ink">{hasDraft ? "Draft preview" : "Preview"}</h3>
            {preview.isFetching ? <Chip signal="ctx">Recalculating…</Chip> : null}
          </div>
          <p className="text-sm text-ink-2">
            {hasDraft ? "What this draft would select, counted live by the engine." : "What the saved selection currently selects, counted live by the engine."}
          </p>
          {preview.isError && isApiError(preview.error) && preview.error.httpStatus === 403 ? (
            <p role="alert" className="text-sm text-danger">The preview was refused for this organisation.</p>
          ) : null}
          {preview.isError && isApiError(preview.error) && preview.error.httpStatus !== 403 ? (
            <ErrorState error={preview.error} deliveryRead onRetry={() => void preview.refetch()} />
          ) : null}
          {preview.isSuccess ? (
            <>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3" aria-label="Preview counts">
                <div><dt className="inv-label">Accepted in pool</dt><dd className="inv-mono mt-2 text-xl text-ink">{preview.data.selection.accepted.toLocaleString("en-GB")}</dd></div>
                <div><dt className="inv-label">High-intent in pool</dt><dd className="inv-mono mt-2 text-xl text-ink">{preview.data.selection.highIntent.toLocaleString("en-GB")}</dd></div>
                <div><dt className="inv-label">Selected by this {hasDraft ? "draft" : "selection"}</dt><dd className="inv-mono mt-2 text-xl text-ink">{preview.data.selection.selected.toLocaleString("en-GB")}</dd></div>
              </dl>
              <p className="text-sm text-ink-2">
                By tag:{" "}
                {(["A", "B", "C"] as const).map((tag) => (
                  <span key={tag} className="ml-2 whitespace-nowrap">Tag {tag}: {preview.data.selection.selectedByTag[tag]?.toLocaleString("en-GB") ?? "0"}</span>
                ))}
              </p>
              <div className="flex flex-col gap-2 rounded-card border border-line bg-surface-muted p-3">
                <h4 className="inv-label">Not selected, and why (overlapping groups)</h4>
                <p className="text-xs text-ink-3">{OVERLAP_NOTE}</p>
                <ul className="flex flex-col gap-1">
                  {preview.data.diagnostics.map((diagnostic) => (
                    <li key={diagnostic.code} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-ink">{DIAGNOSTIC_LABELS[diagnostic.code] ?? diagnostic.code}</span>
                      <span className="inv-mono text-xs text-ink-2">{diagnostic.count.toLocaleString("en-GB")}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-ink-3">
                Evaluated {new Date(preview.data.evaluatedAt).toLocaleString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} UTC
                {preview.data.sourceRevision !== null ? <> · source revision {preview.data.sourceRevision}</> : " · no source revision on record"}
                {preview.data.geography.mode !== null ? <> · geography {preview.data.geography.mode}</> : null}
              </p>
            </>
          ) : null}
        </div>

        <div className="inv-card flex flex-col gap-3 p-5" data-pad>
          <h3 className="text-base font-semibold text-ink">Save</h3>
          <p className="text-sm text-ink-2">
            Saving stores this selection for your organisation. It does not send anything to a receiver.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="primary" className="min-h-11" disabled={savePending || conflict !== null || (workspace.uncertainSave && workspace.reconciliation !== "retry_allowed")} loading={savePending} onClick={submit}>
              Save selection
            </Button>
            {hasDraft ? (
              <Button type="button" variant="secondary" className="min-h-11" disabled={savePending || (workspace.uncertainSave && workspace.reconciliation === null)} onClick={() => { clearDraft(); setRebasedNote(null); }}>
                {workspace.reconciliation === "matching" ? "Use saved selection" : "Discard draft"}
              </Button>
            ) : null}
          </div>
          <p role="status" tabIndex={-1} ref={statusRef} data-save-status="save-status" className="text-sm text-ink-2 focus:outline-none">
            {saveResult !== null ? `Selection saved (revision ${saveResult.revision}). Nothing has been sent.` : workspace.reconciliation === "matching" ? `The saved selection matches your draft at revision ${revision}.` : workspace.reconciliation === "retry_allowed" ? `The saved selection was checked at revision ${revision}. You can retry the original selection.` : workspace.reconciliation === "conflict" ? "A newer saved selection differs from your draft. Review the conflict below." : workspace.uncertainSave ? "Your draft is kept. The save outcome needs checking." : hasDraft ? "Unsaved draft - your edits are kept on this device until you save." : ""}
          </p>
          {conflict !== null ? (
            <div role="alert" className="flex flex-col gap-2 rounded-card border border-red-200 bg-red-50/60 p-4">
              <p className="text-sm font-semibold text-ink">
                The saved selection changed while you were editing{conflict.currentRevision !== null ? ` (server revision ${conflict.currentRevision})` : ""}. Your draft is preserved.
              </p>
              <p className="text-sm text-ink-2">Reload the saved selection to continue; your edits stay and are rebased onto the current revision.</p>
              {conflict.correlationId ? <p className="text-xs text-ink-faint">Correlation id: <code className="font-mono">{conflict.correlationId}</code></p> : null}
              <div>
                <Button type="button" variant="secondary" className="min-h-11" onClick={() => void reloadAfterConflict()}>
                  Reload the saved selection
                </Button>
              </div>
            </div>
          ) : null}
          {rebasedNote ? <p className="text-sm text-ink-2">{rebasedNote}</p> : null}
          {workspace.uncertainSave && conflict === null ? (
            <div role="alert" className="flex flex-col gap-3 rounded-card border border-line bg-surface-muted p-4">
              <p className="text-sm font-semibold text-ink">
                {workspace.reconciliation === "matching" ? `The saved selection matches your draft (revision ${revision}).`
                  : workspace.reconciliation === "retry_allowed" ? `The saved selection is still at revision ${revision}.`
                  : "The save outcome is unknown."}
              </p>
              <p className="text-sm text-ink-2">
                {workspace.reconciliation === "matching" ? "The current saved selection matches these edits. This does not identify which request saved it. Use the saved selection to continue."
                  : workspace.reconciliation === "retry_allowed" ? "You can retry the original selection with its original revision check. An earlier request may still finish; the revision check prevents a second change from the same base."
                  : "The service may have saved your selection. Your draft is kept. Check the saved selection before another attempt."}
              </p>
              {draft?.attempt?.correlationId ? <p className="text-xs text-ink-2">Correlation id: <code className="font-mono">{draft.attempt.correlationId}</code></p> : null}
              {workspace.reconciliation !== "matching" ? (
                <div><Button type="button" variant="secondary" className="min-h-11 min-w-11" disabled={workspace.reconciling} loading={workspace.reconciling} onClick={() => void workspace.reconcileSave()}>
                  Check saved selection
                </Button></div>
              ) : null}
            </div>
          ) : null}
          {workspace.saveError !== null && !workspace.uncertainSave && conflict === null && isApiError(workspace.saveError) ? (
            <ErrorState error={workspace.saveError} onRetry={save} />
          ) : null}
        </div>
      </form>

      <div className="inv-card flex flex-col gap-3 p-5" data-pad>
        <h3 className="text-base font-semibold text-ink">Receiver setup</h3>
        <p className="text-sm text-ink-2">
          These statuses show delivery readiness. Saving your selection does not send leads.
        </p>
        {readiness.isError ? (
          // The readiness read failed on its own (its tenant/config/denied refusals were
          // handled above). Unknown is not missing: no state is inferred, no chip is
          // rendered from the UI's fallback vocabulary, and the retry is explicit.
          <div className="flex flex-col gap-2">
            <ErrorState error={readiness.error} deliveryRead onRetry={() => void readiness.refetch()} />
            <p className="text-sm text-ink-2">
              The receiver setup states are not known until this read succeeds. Try reading them again; your draft is kept.
            </p>
          </div>
        ) : (
          <>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Readiness states">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-line p-3">
            <dt className="text-sm text-ink">Selection</dt>
            <dd className="min-w-0 max-w-full"><Chip className="max-w-full whitespace-normal break-words" signal={readinessData?.selection.state === "configured" ? "buy" : "ctx"}>
              {readinessData?.selection.state === "configured" ? "Configured" : "Setup required"}
            </Chip></dd>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-line p-3">
            <dt className="text-sm text-ink">Campaign binding</dt>
            <dd className="min-w-0 max-w-full"><Chip className="max-w-full whitespace-normal break-words" signal={readinessData?.binding.state === "bound" ? "buy" : "ctx"}>
              {readinessData?.binding.state === "bound" ? "Campaign bound" : "Binding missing"}
            </Chip></dd>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-line p-3">
            <dt className="text-sm text-ink">Receiver destination</dt>
            <dd className="min-w-0 max-w-full"><Chip className="max-w-full whitespace-normal break-words" signal={readinessData?.destination.state === "enabled" ? "buy" : "ctx"}>
              Receiver destination: {readinessData?.destination.state ?? "missing"}
            </Chip></dd>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-line p-3">
            <dt className="text-sm text-ink">Coverage</dt>
            <dd className="min-w-0 max-w-full"><Chip className="max-w-full whitespace-normal break-words" signal={readinessData?.coverage.state === "active" ? "buy" : "ctx"}>
              Coverage sync: {readinessData?.coverage.state ?? "missing"}
            </Chip></dd>
          </div>
        </dl>
        {readinessData !== null && readinessData.sources.length > 0 ? (
          <div>
            <h4 className="inv-label">Source batches (most recent, as the server reports them)</h4>
            <ul className="mt-2 flex flex-col gap-2">
              {readinessData.sources.map((source) => (
                <li key={source.batchId} className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line p-2 text-sm">
                  <span className="inv-mono text-xs text-ink-2">{source.batchId}{source.sourceLabel ? ` · ${source.sourceLabel}` : ""}</span>
                  {/* The PS7/V03 precedent: the amber and red chip pairings fail AA at 12px
                      (3.07:1 and 4.41:1), so unhealthy states carry the neutral chip and the
                      word itself does the work. */}
                  <Chip signal={source.attested && !source.revoked ? "buy" : "ctx"}>
                    {source.revoked ? "Revoked" : source.attested ? "Attested" : "Not attested"}
                  </Chip>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-ink-2">No source batches reported for this campaign yet.</p>
        )}
          </>
        )}
        <div className="flex flex-col gap-1 border-t border-line pt-3">
          <Link to="/high-intent/delivery" className="inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-2 hover:underline">
            View delivery records
          </Link>
          <p className="max-w-3xl text-xs text-ink-3">
            Coverage describes the current sync state reported by the server. It is never shown as an applied send, and a
            receipt is listed in delivery records instead: each receipt there keeps Delivered, Older event received and
            Outcome unknown as separate outcomes.
          </p>
        </div>
      </div>
    </div>
  );
}
